import React, { useEffect, useState } from "react";
import { Alert, Input, Switch, Tooltip, Typography } from "antd";
import { useTranslation } from "react-i18next";
import SearchBarComp from "../../../commons/SearchBar/SearchBar";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import PageToolbar from "../../../design-system/PageToolbar";
import ResponsiveTable from "../../../design-system/ResponsiveTable";
import { getCustomers, saveConsent } from "./api";
import { formatDateTime } from "./labels";

const PAGE_SIZE = 25;

/** Phone number, saved when the field loses focus. */
const PhoneField = ({ customer, onSaved }) => {
  const { t } = useTranslation("dashboard");
  const [value, setValue] = useState(customer.phone);
  useEffect(() => setValue(customer.phone), [customer.phone]);
  const save = async () => {
    if (value === customer.phone) return;
    try {
      const res = await saveConsent(customer._id, { phone: value });
      onSaved(res.data.data);
    } catch (error) {
      setValue(customer.phone);
      generalErrorAlert(error);
    }
  };
  return (
    <Input
      value={value}
      placeholder={t("Mobile number")}
      status={value && !customer.phoneE164 && value === customer.phone ? "warning" : ""}
      onChange={e => setValue(e.target.value)}
      onBlur={save}
      onPressEnter={save}
      maxLength={40}
    />
  );
};

const CustomerConsent = () => {
  const { t, i18n } = useTranslation("dashboard");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ data: [], count: 0 });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getCustomers({ search, page: page - 1, pageSize: PAGE_SIZE });
      setData(res.data);
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [search, page]);

  const replace = row =>
    setData(prev => ({ ...prev, data: prev.data.map(c => (c._id === row._id ? row : c)) }));

  const toggle = async (customer, key, checked) => {
    try {
      const res = await saveConsent(customer._id, { [key]: checked });
      replace(res.data.data);
    } catch (error) {
      generalErrorAlert(error);
    }
  };

  const consentSwitch = (key, label) => ({
    title: label,
    key,
    width: 110,
    align: "center",
    render: (_, row) => (
      <Switch
        size="small"
        aria-label={`${label}: ${row.name}`}
        checked={row.consent[key]}
        onChange={checked => toggle(row, key, checked)}
      />
    )
  });

  const columns = [
    {
      title: t("Customer"),
      key: "name",
      mobile: "title",
      render: (_, row) => (
        <div>
          <Typography.Text strong>{row.name}</Typography.Text>
          <div className="nt-small">{row.email}</div>
        </div>
      )
    },
    {
      title: t("Mobile number"),
      key: "phone",
      mobile: "full",
      render: (_, row) => <PhoneField customer={row} onSaved={replace} />
    },
    consentSwitch("email", t("Email")),
    consentSwitch("sms", t("SMS")),
    consentSwitch("whatsapp", t("WhatsApp")),
    consentSwitch("reviews", t("Review requests")),
    {
      title: t("Recorded"),
      key: "updatedAt",
      width: 150,
      render: (_, row) =>
        row.consent.updatedAt ? (
          <Tooltip title={row.consent.note}>
            {formatDateTime(row.consent.updatedAt, i18n.language)}
          </Tooltip>
        ) : (
          "—"
        )
    }
  ];

  return (
    <div>
      <Alert
        className="nt-gap"
        type="info"
        showIcon
        message={t("Consent (DSGVO)")}
        description={t(
          "Appointment emails are service messages and go out unless the customer objected. SMS and WhatsApp need the customer's consent and mobile number; review requests are advertising and need their own consent. Every change is recorded with date and user."
        )}
      />
      <PageToolbar
        filters={
          <SearchBarComp
            handleSearch={value => {
              setSearch(value);
              setPage(1);
            }}
          />
        }
      />
      <ResponsiveTable
        rowKey="_id"
        loading={loading}
        columns={columns}
        dataSource={data.data}
        mobileAvatar={false}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: data.count,
          showSizeChanger: false,
          onChange: setPage
        }}
      />
    </div>
  );
};

export default CustomerConsent;
